import { createContext, useContext, useReducer, ReactNode, useEffect, useCallback, useRef } from 'react';
import { Product, ProductVariant } from '../lib/api/models';
import api from '../lib/api';
import { useAuth } from './AuthContext';

// Verificación de si estamos en el navegador o en el servidor
const isBrowser = typeof window !== 'undefined';

// Definir tipos para el carrito
export interface CartItemPackaging {
  minimumQuantity: number;
  quantityStep: number;
  presentation?: string;
  description?: string;
}

export interface CartItemDiscountInfo {
  type: 'percentage' | 'fixed';
  value: number;
  displayValue: string;
}

export interface CartItem {
  id: string;
  productId: string;
  variantId: string;
  title: string;
  variantTitle?: string;
  price: number;
  priceBeforeDiscount?: number;
  discountInfo?: CartItemDiscountInfo;
  discountSource?: 'user' | 'global';
  quantity: number;
  imageUrl: string;
  packaging?: CartItemPackaging;
}

interface CartState {
  items: CartItem[];
  totalItems: number;
  subtotal: number;
  isLoading: boolean;
}

// Acciones para el reducer
type CartAction =
  | { type: 'ADD_ITEM'; payload: { product: Product; variant: ProductVariant | null; quantity: number } }
  | { type: 'REMOVE_ITEM'; payload: { itemId: string } }
  | { type: 'UPDATE_QUANTITY'; payload: { itemId: string; quantity: number } }
  | { type: 'CLEAR_CART' }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_CART'; payload: CartState };

interface CartContextType {
  cart: CartState;
  addItem: (product: Product, variant: ProductVariant | null, quantity: number) => void;
  removeItem: (itemId: string) => void;
  updateQuantity: (itemId: string, quantity: number) => void;
  clearCart: () => void;
  refetch: () => Promise<void>;
}

// Crear contexto
const CartContext = createContext<CartContextType | undefined>(undefined);

// Estado inicial del carrito
const initialState: CartState = {
  items: [],
  totalItems: 0,
  subtotal: 0,
  isLoading: false,
};

// Función para guardar el carrito en localStorage
const saveCartToLocalStorage = (cart: CartState) => {
  if (isBrowser) {
    localStorage.setItem('jarad_cart', JSON.stringify(cart));
  }
};

// Función para cargar el carrito desde localStorage
const loadCartFromLocalStorage = (): CartState => {
  if (isBrowser) {
    const savedCart = localStorage.getItem('jarad_cart');
    if (savedCart) {
      try {
        return JSON.parse(savedCart);
      } catch (error) {
        console.error('Error al cargar el carrito:', error);
      }
    }
  }
  return initialState;
};

// Reducer para gestionar el estado del carrito
const cartReducer = (state: CartState, action: CartAction): CartState => {
  switch (action.type) {
    case 'SET_CART': {
      return action.payload;
    }

    case 'ADD_ITEM': {
      if (!isBrowser) return state;

      const { product, variant, quantity } = action.payload;

      // Crear un ID único para este item
      const itemId = variant ? `${product.id}_${variant.sku}` : product.id;

      // Verificar si el producto ya está en el carrito
      const existingItemIndex = state.items.findIndex(item => item.id === itemId);

      let updatedItems;

      if (existingItemIndex >= 0) {
        // Si el producto ya existe, actualizar cantidad
        updatedItems = [...state.items];
        updatedItems[existingItemIndex] = {
          ...updatedItems[existingItemIndex],
          quantity: updatedItems[existingItemIndex].quantity + quantity,
        };
      } else {
        // Si el producto no existe, añadirlo
        const newItem: CartItem = {
          id: itemId,
          productId: product.id,
          variantId: variant?.sku || '',
          title: product.name,
          variantTitle: variant?.name || '',
          price: variant?.price || product.price || 0,
          quantity,
          imageUrl: product.images[0]?.url || '',
        };
        updatedItems = [...state.items, newItem];
      }

      // Recalcular totales
      const totalItems = updatedItems.reduce((sum, item) => sum + item.quantity, 0);
      const subtotal = updatedItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);

      const newState = {
        ...state,
        items: updatedItems,
        totalItems,
        subtotal,
      };

      saveCartToLocalStorage(newState);
      return newState;
    }

    case 'REMOVE_ITEM': {
      if (!isBrowser) return state;

      const { itemId } = action.payload;

      // Filtrar el item a eliminar
      const updatedItems = state.items.filter(item => item.id !== itemId);

      // Recalcular totales
      const totalItems = updatedItems.reduce((sum, item) => sum + item.quantity, 0);
      const subtotal = updatedItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);

      const newState = {
        ...state,
        items: updatedItems,
        totalItems,
        subtotal,
      };

      saveCartToLocalStorage(newState);
      return newState;
    }

    case 'UPDATE_QUANTITY': {
      if (!isBrowser) return state;

      const { itemId, quantity } = action.payload;

      if (quantity <= 0) {
        // Si la cantidad es 0 o negativa, eliminar el item
        return cartReducer(state, { type: 'REMOVE_ITEM', payload: { itemId } });
      }

      // Actualizar la cantidad del item
      const updatedItems = state.items.map(item =>
        item.id === itemId ? { ...item, quantity } : item
      );

      // Recalcular totales
      const totalItems = updatedItems.reduce((sum, item) => sum + item.quantity, 0);
      const subtotal = updatedItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);

      const newState = {
        ...state,
        items: updatedItems,
        totalItems,
        subtotal,
      };

      saveCartToLocalStorage(newState);
      return newState;
    }

    case 'CLEAR_CART': {
      if (!isBrowser) return state;

      saveCartToLocalStorage(initialState);
      return initialState;
    }

    case 'SET_LOADING': {
      return {
        ...state,
        isLoading: action.payload,
      };
    }

    default:
      return state;
  }
};

// Proveedor del contexto
export const CartProvider = ({ children }: { children: ReactNode }) => {
  const [cart, dispatch] = useReducer(cartReducer, initialState);
  const { auth } = useAuth();
  const syncSequence = useRef(0);

  // Función para sincronizar el carrito desde el backend - SIEMPRE hace fetch
  const syncCartFromBackend = useCallback(async () => {
    if (!isBrowser) return;
    const requestId = ++syncSequence.current;
    const requestToken = api.client.getAuthToken();
    const isCurrentRequest = () => requestId === syncSequence.current && requestToken === api.client.getAuthToken();

    console.log('🛒 [CartContext] syncCartFromBackend called - fetching from API');
    dispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await api.cart.getCart();
      if (!isCurrentRequest()) return;
      console.log('🛒 [CartContext] API response:', response);
      if (response.success && response.data) {
        const backendCart = response.data;
        // Transformar los items del backend al formato del contexto
        const items: CartItem[] = (backendCart.items || []).map((item: any) => ({
          id: item.variantSku ? `${item.productId}_${item.variantSku}` : item.productId,
          productId: item.productId,
          variantId: item.variantSku || '',
          title: item.title || item.name || '',
          variantTitle: item.variantTitle || item.variantName || '',
          price: item.price || 0,
          quantity: item.quantity || 1,
          imageUrl: item.imageUrl || item.image || '',
          packaging: item.packaging ? {
            minimumQuantity: item.packaging.minimumQuantity || 1,
            quantityStep: item.packaging.quantityStep || 1,
            presentation: item.packaging.presentation || '',
            description: item.packaging.description || ''
          } : undefined,
          priceBeforeDiscount: item.priceBeforeDiscount || undefined,
          discountInfo: item.discountInfo || undefined,
          discountSource: item.discountSource || undefined
        }));

        const newState: CartState = {
          items,
          totalItems: backendCart.totalItems || items.reduce((sum, i) => sum + i.quantity, 0),
          subtotal: backendCart.subtotal || items.reduce((sum, i) => sum + i.price * i.quantity, 0),
          isLoading: false
        };

        console.log('🛒 [CartContext] Setting cart state with', items.length, 'items');
        dispatch({ type: 'SET_CART', payload: newState });
        saveCartToLocalStorage(newState);
      }
    } catch (error) {
      if (!isCurrentRequest()) return;
      console.error('Error al sincronizar carrito desde el backend:', error);
      // Si falla, cargar desde localStorage como fallback
      const savedCart = loadCartFromLocalStorage();
      if (savedCart.items.length > 0) {
        dispatch({ type: 'SET_CART', payload: savedCart });
      }
    } finally {
      if (isCurrentRequest()) dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, []);

  // Cargar al iniciar y cuando cambia la sesión, incluido el cierre de sesión.
  useEffect(() => {
    if (!isBrowser) return;
    syncCartFromBackend();
    return () => { syncSequence.current += 1; };
  }, [auth.isAuthenticated, auth.user, syncCartFromBackend]);

  // Escuchar evento de actualización de carrito desde otras partes de la app
  useEffect(() => {
    if (!isBrowser) return;

    const handleCartUpdate = (event: CustomEvent) => {
      console.log('🛒 [CartContext] jarad-cart-updated event received', event.detail);
      // Si el evento trae datos del carrito, usarlos directamente
      if (event.detail && event.detail.items) {
        syncSequence.current += 1;
        const backendCart = event.detail;
        const items: CartItem[] = (backendCart.items || []).map((item: any) => ({
          id: item.variantSku ? `${item.productId}_${item.variantSku}` : item.productId,
          productId: item.productId,
          variantId: item.variantSku || '',
          title: item.title || item.name || '',
          variantTitle: item.variantTitle || item.variantName || '',
          price: item.price || 0,
          quantity: item.quantity || 1,
          imageUrl: item.imageUrl || item.image || '',
          packaging: item.packaging ? {
            minimumQuantity: item.packaging.minimumQuantity || 1,
            quantityStep: item.packaging.quantityStep || 1,
            presentation: item.packaging.presentation || '',
            description: item.packaging.description || ''
          } : undefined,
          priceBeforeDiscount: item.priceBeforeDiscount || undefined,
          discountInfo: item.discountInfo || undefined,
          discountSource: item.discountSource || undefined
        }));

        const newState: CartState = {
          items,
          totalItems: backendCart.totalItems || items.reduce((sum, i) => sum + i.quantity, 0),
          subtotal: backendCart.subtotal || items.reduce((sum, i) => sum + i.price * i.quantity, 0),
          isLoading: false
        };

        console.log('🛒 [CartContext] Using event data directly, items:', items.length, 'totalItems:', newState.totalItems);
        dispatch({ type: 'SET_CART', payload: newState });
        saveCartToLocalStorage(newState);
      } else {
        // Fallback: hacer fetch al backend
        syncCartFromBackend();
      }
    };

    // Handle bfcache (back/forward cache) restoration
    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        console.log('🛒 [CartContext] Page restored from bfcache - refetching');
        syncCartFromBackend();
      }
    };

    // Refetch when window gains focus
    const handleFocus = () => {
      console.log('🛒 [CartContext] Window focused - refetching');
      syncCartFromBackend();
    };

    // Refetch when page becomes visible
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        console.log('🛒 [CartContext] Page became visible - refetching');
        syncCartFromBackend();
      }
    };

    window.addEventListener('jarad-cart-updated', handleCartUpdate as EventListener);
    window.addEventListener('pageshow', handlePageShow);
    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('jarad-cart-updated', handleCartUpdate as EventListener);
      window.removeEventListener('pageshow', handlePageShow);
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [syncCartFromBackend]);

  // Funciones para manipular el carrito
  const addItem = async (product: Product, variant: ProductVariant | null, quantity: number) => {
    // Primero actualizar el estado local para UI inmediata
    dispatch({ type: 'ADD_ITEM', payload: { product, variant, quantity } });

    // Luego sincronizar con el backend
    if (isBrowser) {
      try {
        console.log('🛒 [CartContext] Calling API addToCart:', {
          productId: product.id,
          quantity,
          variantSku: variant?.sku
        });
        // Siempre enviar el ID del producto principal
        // Si hay variante, enviar también el SKU de la variante
        const response = await api.cart.addToCart(product.id, quantity, variant?.sku);

        // Notify with response data to avoid stale GET request
        if (response.success && response.data) {
          console.log('🛒 [CartContext] addItem response:', response.data);
          window.dispatchEvent(new CustomEvent('jarad-cart-updated', { detail: response.data }));
        }
      } catch (error) {
        console.error('Error al añadir al carrito en el servidor:', error);
        // No revertir el cambio local, el carrito local actúa como fallback
      }
    }
  };

  const removeItem = async (itemId: string) => {
    // Primero actualizar el estado local
    dispatch({ type: 'REMOVE_ITEM', payload: { itemId } });

    // Luego sincronizar con el backend
    if (isBrowser) {
      try {
        // Extraer el productId y variantSku del itemId
        // itemId formato: productId_variantSku o solo productId
        const parts = itemId.split('_');
        const productId = parts[0];
        const variantSku = parts.length > 1 ? parts[1] : undefined;

        const response = await api.cart.removeFromCart(productId, variantSku);

        // Notify with response data to avoid stale GET request
        if (response.success && response.data) {
          window.dispatchEvent(new CustomEvent('jarad-cart-updated', { detail: response.data }));
        }
      } catch (error) {
        console.error('Error al eliminar del carrito en el servidor:', error);
      }
    }
  };

  const updateQuantity = async (itemId: string, quantity: number) => {
    // Primero actualizar el estado local
    dispatch({ type: 'UPDATE_QUANTITY', payload: { itemId, quantity } });

    // Luego sincronizar con el backend
    if (isBrowser) {
      try {
        // Extraer el productId y variantSku del itemId
        const parts = itemId.split('_');
        const productId = parts[0];
        const variantSku = parts.length > 1 ? parts[1] : undefined;

        const response = await api.cart.updateCartItem(productId, quantity, variantSku);

        // Notify with response data to avoid stale GET request
        if (response.success && response.data) {
          window.dispatchEvent(new CustomEvent('jarad-cart-updated', { detail: response.data }));
        }
      } catch (error) {
        console.error('Error al actualizar cantidad en el servidor:', error);
      }
    }
  };

  const clearCart = async () => {
    // Primero actualizar el estado local
    dispatch({ type: 'CLEAR_CART' });

    // Luego sincronizar con el backend
    if (isBrowser) {
      try {
        const response = await api.cart.clearCart();

        // Notify with response data to avoid stale GET request
        if (response.success && response.data) {
          window.dispatchEvent(new CustomEvent('jarad-cart-updated', { detail: response.data }));
        }
      } catch (error) {
        console.error('Error al limpiar carrito en el servidor:', error);
      }
    }
  };

  return (
    <CartContext.Provider
      value={{
        cart,
        addItem,
        removeItem,
        updateQuantity,
        clearCart,
        refetch: syncCartFromBackend,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

// Hook personalizado para usar el contexto
export const useCart = (): CartContextType => {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart debe usarse dentro de un CartProvider');
  }
  return context;
};

export default CartContext;
